import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { styled } from "@ff/ui";
import Box from "@ff/ui/Box";
import Loader from 'client/components/Loader';
import Model from './Model';
import useVehicleModels from 'client/hooks/queries/useVehicleModels';
import useUser from 'client/hooks/queries/useUser';
import useOrderDetail from 'client/hooks/queries/useOrderDetail';
import constants from 'client/v3/constants';
import useVehicleModelDetails from 'client/hooks/queries/useVehicleModelDetails';
import useConfig from 'client/hooks/useConfig';
import url from 'client/utils/url';
import useModelSectionAligned from './useModelSectionAligned';
import modelCodes from 'client/constants/modelCode';
import appHandlers from 'client/utils/appHandlers';
import { ConfigContext } from 'client/containers/ConfigContextProvider';

const { HEADER_HEIGHT } = constants;

export default function ModelsPage(){
  const history = useHistory();
  const qsParams = useConfig();
  const { ORDER: orderNum } = useConfig()

  const { vehicleModels, loading: loadingVehicleModels } = useVehicleModels();
  const { user, loading: loadingUser } = useUser();
  const { order, loading: loadingOrder } = useOrderDetail(orderNum as string, {
    skip: !orderNum?.length || !user
  });
  const { vehicleModelDetails, loading: loadingVehicleModelDetails } =
    useVehicleModelDetails(order?.vehicleModel.id as number, {
      skip: !order?.vehicleModel.id
    });

  const loading = loadingVehicleModels ||
    loadingUser ||
    loadingOrder ||
    loadingVehicleModelDetails;

  const [modelSectionHeightsRefs, modelSectionHeights] =
    useModelSectionAligned(vehicleModels?.length || 0, loading);

  const {
    isMobileLayout,
  } = useContext(ConfigContext);

  if (loading) {
    return <Loader />;
  }

  let orderConfiguration;
  if (order) {
    const options = {};
    // if the order is a legacy preorder, the vehicleConfigurationCore may be null.
    order.vehicleConfigurationCore?.vehicleConfigurationItems?.forEach(vci => {
      options[vci.vehicleOption.vehicleOptionType.categoryCode]
        = vci.vehicleOption.vehicleOptionType.typeCode;
    });

    if (
      (!order.canDowngradeEdition &&
        order.vehicleModel.fullModelCode ===
          modelCodes.FF91FuturistAlliance.fullModelCode) ||
      (!order.canUpgradeEdition &&
        order.vehicleModel.fullModelCode ===
          modelCodes.FF91Futurist.fullModelCode)
    ) {
      // In App, if use try to go back to a invalid model page from options page, close the configurator directly.
      if(qsParams.APPONLY === '1' && qsParams.APP_GOBACK_FROM === 'options' ){
        setTimeout(appHandlers.getHandlerByKey('CloseConfigurator'), 10);

        return null;
      }

      Object.keys(options).forEach((k) => {
        qsParams[k] = options[k];
      });
      qsParams['VEH'] = order.vehicleModel.fullModelCode;

      history.replace(url.getPageNavigationV3('options', qsParams));
      return null;
    }

    const image =
      vehicleModelDetails?.vehicleConfigurationAssets.exterior.color.typeValues.find(
        (v) => v.code === options['EXC']
      )?.images[0];

    orderConfiguration = {
      orderNum: order.orderNum,
      exteriorImage: image,
      modelCode: order.vehicleModel.fullModelCode,
      options,
      canUpgradeEdition: order.canUpgradeEdition,
      canDowngradeEdition: order.canDowngradeEdition,
    };

  } else {
    if(orderNum){
      history.replace(url.getPageNavigationV3('model'));
      return null;
    }
  }

    return (
      <Container>
        {vehicleModels?.map((vm, i) => {
          const status =
            vm.vehicleMarketStatusDesc === 'PreOrder' && vm.isWaitlistAllowed
              ? 'Waitlist'
              : vm.vehicleMarketStatusDesc;
          const coverImage = (isMobileLayout && vm.vehicleOverviewAssets.coverImage.mobile)
            ? vm.vehicleOverviewAssets.coverImage.mobile
            : vm.vehicleOverviewAssets.coverImage.desktop
          return (
            <ModelContainer key={vm.fullModelCode} flex={order ? 'unset' : '1'}>
              <Model
                code={vm.fullModelCode}
                name={vm.name}
                coverImage={coverImage}
                features={vm.vehicleOverviewAssets.overviewFeatures.map(
                  (f) => f.title
                )}
                specs={vm.vehicleOverviewAssets.overviewSpecs || []}
                status={status}
                ref={(ref) => {
                  if(ref) {
                    modelSectionHeightsRefs.current[i] = ref
                  }
                }}
                sectionHeights={modelSectionHeights || undefined}
                order={orderConfiguration}
              />
            </ModelContainer>
          );
        })}
      </Container>
    );
}

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.colors.disabledBackground,
  padding: '24px',
  gap: '24px',

  [theme.mediaQueries.md]: {
    padding: '38px',
    gap: '38px',
  },

  [theme.mediaQueries.lg]: {
    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
    padding: '70px',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    minWidth: 'unset',
  }
}));

const ModelContainer = styled(Box)({
  maxWidth: '460px',
});
